import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, ShieldCheck, Wrench, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/contexts/LanguageContext";
import { MEDIA, SITE } from "@/data/site";

const About = () => {
  const { t } = useLang();
  const values = t("about.values");

  return (
    <div data-testid="page-about">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pt-16 sm:pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#082133] font-semibold">
              {t("about.eyebrow")}
            </span>
            <h1 className="mt-4 font-display font-semibold text-[#111111] text-[40px] sm:text-[54px] lg:text-[64px] leading-[1.05] tracking-[-0.03em]">
              {t("about.title")}
            </h1>
            <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-[#4B5563]">
              {t("about.lead")}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5]">
              <div className="absolute inset-0 rounded-[40px] overflow-hidden shadow-[0_40px_80px_-30px_rgba(8,33,51,0.3)]">
                <img
                  src={MEDIA.about}
                  alt="Technician working on appliance repair"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 rounded-2xl bg-[#082133] text-[#FAFAFA] px-5 py-4 shadow-xl">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#FAFAFA]/65">
                  {t("common.proudlyServing")}
                </div>
                <div className="mt-1 font-display font-semibold flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> {SITE.region}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: MapPin, k: "pillarTitle1", b: "pillarBody1" },
            { icon: Wrench, k: "pillarTitle2", b: "pillarBody2" },
            { icon: Heart, k: "pillarTitle3", b: "pillarBody3" },
          ].map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={i}
                data-testid={`about-pillar-${i}`}
                className="rounded-3xl bg-white border border-[#E5E7EB] p-8 sm:p-10 transition-all hover:-translate-y-1 hover:shadow-[0_25px_50px_-20px_rgba(8,33,51,0.15)]"
              >
                <span className="h-12 w-12 inline-flex items-center justify-center rounded-2xl bg-[#DCE3DE] text-[#082133]">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-6 font-display text-2xl font-semibold text-[#111111] tracking-tight">
                  {t(`about.${p.k}`)}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#4B5563]">
                  {t(`about.${p.b}`)}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#082133] font-semibold">
              {t("about.eyebrow")}
            </span>
            <h2 className="mt-4 font-display font-semibold text-[#111111] text-[34px] sm:text-[44px] leading-[1.05] tracking-[-0.025em]">
              {t("about.valuesTitle")}
            </h2>
            <div className="mt-8">
              <Link to="/contact">
                <Button
                  data-testid="about-contact-cta"
                  className="rounded-full h-12 px-7 bg-[#082133] text-[#FAFAFA] hover:bg-[#0A2D47]"
                >
                  {t("common.requestService")}
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E5E7EB] rounded-3xl overflow-hidden">
            {(values || []).map((v, i) => (
              <div
                key={i}
                data-testid={`value-${i}`}
                className="bg-[#FAFAFA] p-8 sm:p-10"
              >
                <div className="flex items-center gap-3">
                  <span className="h-8 w-8 rounded-full bg-[#082133] text-[#FAFAFA] inline-flex items-center justify-center text-xs font-semibold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="font-display text-xl font-semibold text-[#111111]">
                    {v.k}
                  </div>
                </div>
                <p className="mt-4 text-[15px] text-[#4B5563] leading-relaxed">
                  {v.v}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust band */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-24">
        <div className="rounded-[32px] bg-white border border-[#E5E7EB] p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center gap-8">
          <span className="h-14 w-14 inline-flex items-center justify-center rounded-2xl bg-[#DCE3DE] text-[#082133]">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <div className="flex-1">
            <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[#111111]">
              {t("about.pillarTitle2")}
            </h3>
            <p className="mt-2 text-[#4B5563] text-[15px] leading-relaxed">
              {t("about.pillarBody2")}
            </p>
          </div>
          <Link to="/services" className="flex-shrink-0">
            <Button
              variant="outline"
              data-testid="about-services-link"
              className="rounded-full h-12 px-7 border-[#082133]/20 text-[#082133] hover:bg-[#082133]/5"
            >
              {t("common.ourServices")}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
